/**
 * Notes API - PocketBase version
 * Replaces src/api/notes.js for PocketBase backend
 */

import pb from '../pocketbase';
import { encryptNoteContent, decryptNoteContent } from '../utils/encryption';

const ENC_PREFIX = 'sense:enc:v1:';

/**
 * Helper to decrypt a single note
 */
async function decryptNote(note) {
    if (!note) return null;

    const content = note.content;

    if (content && content.startsWith(ENC_PREFIX)) {
        try {
            const payload = content.slice(ENC_PREFIX.length);
            const [iv, ciphertext] = payload.split(':');

            if (iv && ciphertext) {
                const decryptedContent = await decryptNoteContent(ciphertext, iv);
                return {
                    ...note,
                    content: decryptedContent
                };
            }
        } catch (e) {
            console.error('Error unpacking note:', e);
            return {
                ...note,
                content: '[Ошибка данных]'
            };
        }
    }

    return note;
}

/**
 * Helper to decrypt a list of notes
 */
async function decryptNotes(notes) {
    if (!notes) return [];
    return Promise.all(notes.map(note => decryptNote(note)));
}

/**
 * Сохраняет заметку пользователя к карточке игры
 */
export async function saveUserNote(gameId, cardId, noteText) {
    try {
        if (!pb.authStore.isValid) {
            throw new Error("Not authenticated");
        }

        // Encrypt content
        const encryptedData = await encryptNoteContent(noteText);
        if (!encryptedData) throw new Error("Encryption failed");

        const packedContent = `${ENC_PREFIX}${encryptedData.content_iv}:${encryptedData.content_ciphertext}`;

        const data = await pb.collection('notes').create({
            user: pb.authStore.model.id,
            game: gameId,
            card: cardId,
            content: packedContent,
        });

        return {
            ...data,
            content: noteText,
            updatedAt: data.updated,
            createdAt: data.created,
        };
    } catch (error) {
        console.error('Ошибка в saveUserNote:', error);
        throw error;
    }
}

/**
 * Получает все активные заметки пользователя к игре
 */
export async function getUserNotesForGame(gameId) {
    try {
        if (!pb.authStore.isValid) throw new Error("Not authenticated");

        const records = await pb.collection('notes').getFullList({
            filter: `user = '${pb.authStore.model.id}' && game = '${gameId}' && deleted_at = null`,
            sort: '-updated',
        });

        return await decryptNotes(records);
    } catch (error) {
        console.error('Ошибка в getUserNotesForGame:', error);
        throw error;
    }
}

/**
 * Получает заметку пользователя к конкретной карточке
 */
export async function getUserNoteForCard(cardId) {
    try {
        if (!pb.authStore.isValid) return null;

        const records = await pb.collection('notes').getList(1, 1, {
            filter: `user = '${pb.authStore.model.id}' && card = '${cardId}' && deleted_at = null`,
            sort: '-updated',
        });

        if (records.items.length === 0) return null;

        return await decryptNote(records.items[0]);
    } catch (error) {
        console.error('Ошибка в getUserNoteForCard:', error);
        throw error;
    }
}

/**
 * Получает все заметки пользователя к конкретной карточке
 */
export async function getAllUserNotesForCard(cardId) {
    try {
        if (!pb.authStore.isValid) return [];

        const records = await pb.collection('notes').getFullList({
            filter: `user = '${pb.authStore.model.id}' && card = '${cardId}' && deleted_at = null`,
            sort: '-updated',
        });

        return await decryptNotes(records);
    } catch (error) {
        console.error('Ошибка в getAllUserNotesForCard:', error);
        throw error;
    }
}

/**
 * Мягкое удаление заметки
 */
export async function softDeleteNote(noteId) {
    try {
        await pb.collection('notes').update(noteId, {
            deleted_at: new Date().toISOString(),
        });
    } catch (error) {
        console.error('Ошибка при удалении заметки:', error);
        throw error;
    }
}

/**
 * Восстановление заметки
 */
export async function restoreNote(noteId) {
    try {
        await pb.collection('notes').update(noteId, {
            deleted_at: null,
        });
    } catch (error) {
        console.error('Ошибка при восстановлении заметки:', error);
        throw error;
    }
}

/**
 * Получает все активные заметки пользователя (для всех игр)
 */
export async function getAllUserNotes() {
    try {
        if (!pb.authStore.isValid) throw new Error("Not authenticated");

        const records = await pb.collection('notes').getFullList({
            filter: `user = '${pb.authStore.model.id}' && deleted_at = null`,
            sort: '-updated',
            expand: 'game,card',
        });

        const decryptedData = await decryptNotes(records);

        return decryptedData.map(note => {
            const game = note.expand?.game;
            const card = note.expand?.card;

            return {
                id: note.id,
                content: note.content,
                updatedAt: note.updated,
                createdAt: note.created,
                gameId: note.game,
                gameTitle: game?.title || '',
                gameSubtitle: game?.subtitle || '',
                gameCoverPath: game?.cover || '',
                gameSlug: game?.slug || '',
                cardId: note.card,
                cardQuestion: card?.question || '',
                cardSubquestion: card?.subquestion || '',
                cardImagePath: card?.image || '',
                cardIdx: card?.idx || 0
            };
        });
    } catch (error) {
        console.error('Ошибка в getAllUserNotes:', error);
        throw error;
    }
}

export default {
    saveUserNote,
    getUserNotesForGame,
    getUserNoteForCard,
    getAllUserNotesForCard,
    softDeleteNote,
    restoreNote,
    getAllUserNotes,
};
